import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getQuoteById, deleteQuote, updateQuoteStatus } from "@/services/quoteService";
import type { Quote, FirestoreDate } from "@/types/quote";
import {
  ArrowLeft,
  Pencil,
  Send,
  Printer,
  Trash2,
  User,
  ShoppingCart,
  CreditCard,
  StickyNote,
  Calendar,
  Mail,
  Phone,
  MapPin,
  Building,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import { queryKeys } from "@/lib/queryKeys";

/** Convierte una fecha de Firestore a Date nativo. */
function toDate(val: FirestoreDate | undefined | null): Date | null {
  if (!val) return null;
  if (val instanceof Date) return val;
  if (typeof (val as { toDate?: () => Date }).toDate === "function") {
    return (val as { toDate(): Date }).toDate();
  }
  if ("seconds" in val) {
    return new Date(val.seconds * 1000);
  }
  return null;
}

/** Formatea una fecha de Firestore a string legible. */
function formatDate(val: FirestoreDate | undefined | null): string {
  const d = toDate(val);
  if (!d) return "--";
  return d.toLocaleDateString("es-DO", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

/** Formatea un monto a moneda. */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("es-DO", {
    style: "currency",
    currency: "DOP",
    minimumFractionDigits: 0,
  }).format(amount);
}

const STATUS_LABELS: Record<string, string> = {
  DRAFT: "Borrador",
  SENT: "Enviada",
  VIEWED: "Vista",
  EXPIRED: "Expirada",
};

function statusBadgeClass(status: string): string {
  switch (status) {
    case "DRAFT":
      return "bg-gray-100 text-gray-700 border-gray-300";
    case "SENT":
      return "bg-blue-100 text-blue-700 border-blue-300";
    case "VIEWED":
      return "bg-green-100 text-green-700 border-green-300";
    case "EXPIRED":
      return "bg-red-100 text-red-700 border-red-300";
    default:
      return "";
  }
}

export function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: rawQuote, isLoading: loading } = useQuery({
    queryKey: queryKeys.quote(id!),
    queryFn: () => getQuoteById(id!),
    enabled: Boolean(id),
  });

  const quote = rawQuote as Quote | null | undefined;

  const handleDelete = async () => {
    if (!id) return;
    if (!confirm("¿Eliminar esta cotización? Esta acción no se puede deshacer.")) return;
    try {
      await deleteQuote(id);
      toast.success("Cotización eliminada");
      await queryClient.invalidateQueries({ queryKey: queryKeys.quotes });
      navigate("/quotes");
    } catch {
      toast.error("Error al eliminar la cotización");
    }
  };

  const handleMarkSent = async () => {
    if (!id) return;
    try {
      await updateQuoteStatus(id, "SENT");
      toast.success("Cotización marcada como enviada");
      await queryClient.invalidateQueries({ queryKey: queryKeys.quote(id) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.quotes });
    } catch {
      toast.error("Error al actualizar el estado");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/quotes">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <h1 className="font-display text-2xl font-semibold">Cotización</h1>
        </div>
        <p className="text-muted-foreground">Cargando...</p>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/quotes">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <h1 className="font-display text-2xl font-semibold">Cotización</h1>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="mx-auto h-12 w-12 text-muted-foreground/50" />
            <p className="mt-4 text-muted-foreground">Cotización no encontrada</p>
            <Button asChild className="mt-4">
              <Link to="/quotes">Volver a cotizaciones</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-6 print:space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between print:hidden">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/quotes">
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Volver</span>
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-display text-2xl font-semibold">{quote.quoteNumber}</h1>
              <Badge variant="outline" className={statusBadgeClass(quote.status)}>
                {STATUS_LABELS[quote.status] ?? quote.status}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              Creada el {formatDate(quote.createdAt)}
              {quote.validUntil && ` · Válida hasta ${formatDate(quote.validUntil)}`}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="gap-2" asChild>
            <Link to={`/quotes/edit/${quote.id}`}>
              <Pencil className="h-4 w-4" />
              Editar
            </Link>
          </Button>
          {quote.status === "DRAFT" && (
            <Button variant="outline" className="gap-2" onClick={handleMarkSent}>
              <Send className="h-4 w-4" />
              Marcar como Enviada
            </Button>
          )}
          <Button variant="outline" className="gap-2" onClick={handlePrint}>
            <Printer className="h-4 w-4" />
            Descargar PDF
          </Button>
          <Button variant="outline" className="gap-2 text-destructive" onClick={handleDelete}>
            <Trash2 className="h-4 w-4" />
            Eliminar
          </Button>
        </div>
      </div>

      {/* Print header */}
      <div className="hidden print:block">
        <h1 className="text-2xl font-semibold">{quote.quoteNumber}</h1>
        <p className="text-sm text-muted-foreground">
          {formatDate(quote.createdAt)} · {STATUS_LABELS[quote.status] ?? quote.status}
        </p>
      </div>

      {/* Customer Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Información del Cliente
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex items-start gap-2">
              <User className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <div>
                <p className="font-medium">{quote.customer?.name ?? "--"}</p>
                <p className="text-xs text-muted-foreground">
                  {quote.customer?.customerType === "business" ? "Empresa" : "Persona Física"}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Mail className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <p>{quote.customer?.email ?? "--"}</p>
            </div>
            <div className="flex items-start gap-2">
              <Phone className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <p>{quote.customer?.phone ?? "--"}</p>
            </div>
            {quote.customer?.address && (
              <div className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 text-muted-foreground" />
                <p>{quote.customer.address}</p>
              </div>
            )}
            {quote.customer?.company && (
              <div className="flex items-start gap-2">
                <Building className="mt-0.5 h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="font-medium">{quote.customer.company}</p>
                  {quote.customer.taxId && (
                    <p className="text-xs text-muted-foreground">RNC: {quote.customer.taxId}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Items */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Vehículos ({quote.items?.length ?? 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Vehículo</TableHead>
                <TableHead className="text-right">Cantidad</TableHead>
                <TableHead className="text-right">Precio Unit.</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(quote.items ?? []).map((item, idx) => (
                <TableRow key={`${item.vehicleId}-${idx}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {item.vehicleImage ? (
                        <img
                          src={item.vehicleImage}
                          alt=""
                          className="h-10 w-10 rounded object-cover border border-border print:hidden"
                        />
                      ) : null}
                      <div>
                        <p className="font-medium">{item.vehicleName}</p>
                        {item.vehicleBrand && (
                          <p className="text-xs text-muted-foreground">{item.vehicleBrand}</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">{item.quantity}</TableCell>
                  <TableCell className="text-right">{formatCurrency(item.unitPrice)}</TableCell>
                  <TableCell className="text-right font-medium">{formatCurrency(item.totalPrice)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Financial Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Resumen Financiero
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="font-medium">{formatCurrency(quote.subtotal ?? 0)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                ITBIS ({((quote.taxRate ?? 0.18) * 100).toFixed(0)}%)
              </span>
              <span className="font-medium">{formatCurrency(quote.taxAmount ?? 0)}</span>
            </div>
            <div className="flex justify-between border-t pt-3 text-lg font-semibold">
              <span>Total</span>
              <span>{formatCurrency(quote.totalAmount ?? 0)}</span>
            </div>

            {quote.financing && (
              <div className="mt-4 rounded-md bg-muted p-4 space-y-2">
                <p className="font-medium text-sm">Financiamiento</p>
                <div className="grid gap-2 sm:grid-cols-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Inicial</span>
                    <span>{formatCurrency(quote.financing.downPayment ?? 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Plazo</span>
                    <span>{quote.financing.financingTerm} meses</span>
                  </div>
                  {quote.financing.interestRate != null && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Tasa de interés</span>
                      <span>{quote.financing.interestRate}%</span>
                    </div>
                  )}
                  {quote.financing.monthlyPayment != null && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Cuota mensual</span>
                      <span className="font-semibold">{formatCurrency(quote.financing.monthlyPayment)}</span>
                    </div>
                  )}
                  {quote.financing.totalFinanced != null && (
                    <div className="flex justify-between sm:col-span-2">
                      <span className="text-muted-foreground">Total financiado</span>
                      <span>{formatCurrency(quote.financing.totalFinanced)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      {(quote.notes || quote.internalNotes) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <StickyNote className="h-5 w-5" />
              Notas
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {quote.notes && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Notas para el cliente</p>
                <p className="whitespace-pre-wrap text-sm">{quote.notes}</p>
              </div>
            )}
            {quote.internalNotes && (
              <div className="print:hidden">
                <p className="text-sm font-medium text-muted-foreground mb-1">Notas internas</p>
                <p className="whitespace-pre-wrap text-sm">{quote.internalNotes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Metadata */}
      <Card className="print:hidden">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              Creada: {formatDate(quote.createdAt)}
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              Actualizada: {formatDate(quote.updatedAt)}
            </div>
            {quote.validUntil && (
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Válida hasta: {formatDate(quote.validUntil)}
              </div>
            )}
            {quote.sentToCustomer && (
              <div className="flex items-center gap-1">
                <Send className="h-4 w-4" />
                Enviada al cliente
              </div>
            )}
            {quote.customerViewed && (
              <div className="flex items-center gap-1">
                Vista por el cliente: {formatDate(quote.customerViewedAt)}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
